import {
  MonitoringParameter,
  TelemetryData,
  MonitoringKPI,
  MonitoringEventsResponse,
  MonitoringEvent,
  TimeseriesField
} from "../types";
import moment from "moment";
import axiosInstance from "../../axiosInstance";
import { store } from "../../store";

// Helper to simulate network latency
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

class MonitoringService {
  private static normalizeFieldName = (value: string) =>
    value.replace(/[_\s-]/g, "").toLowerCase();

  private static toAvgCellTempCelsius = (rawValue: number | string): number => {
    const rawNumeric =
      typeof rawValue === "number" ? rawValue : Number(rawValue);
    if (!Number.isFinite(rawNumeric)) return Number.NaN;

    // Backend can return scaled Fahrenheit-like values; evaluate common scales.
    const scaleCandidates = [1, 10, 100, 1000];
    const idealCelsius = 12;

    let best = Number.NaN;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const scale of scaleCandidates) {
      const fahrenheit = rawNumeric / scale;
      const celsius = (fahrenheit - 32) / 1.8;
      if (!Number.isFinite(celsius) || celsius < -80 || celsius > 150) continue;
      const distance = Math.abs(celsius - idealCelsius);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = celsius;
      }
    }

    return Number.isFinite(best) ? best : (rawNumeric - 32) / 1.8;
  };

  private static adaptTelemetryValueForField = (
    fieldName: string,
    rawValue: number | string
  ): number | string => {
    const normalized = this.normalizeFieldName(fieldName);
    if (
      normalized === "avgcelltemp" ||
      normalized === "averagecelltemperature"
    ) {
      return this.toAvgCellTempCelsius(rawValue);
    }
    return rawValue;
  };

  private static getFriendlyFieldName = (rawName: string): string => {
    const normalized = this.normalizeFieldName(rawName);
    const friendlyNames: Record<string, string> = {
      crdhours: "CRD Hours",
      inverterhours: "Inverter Hours",
      motorhours: "Motor Hours",
      soc: "SoC",
      systemstate: "System State",
      sysfailurestate: "System Failure State",
      zivandisplayalarm: "Zivan Display Alarm",
      crddpfsootperc: "CRD DPF Soot Percentage",
      dpfc1regenreqlampstatus: "DPFC1 Regen Request (Lamp Status)",
      dtc: "DTC",
      regenlevel: "Regen Level",
      immpowermode: "Imm Power Mode",
      inmpowermodeactive: "Imm Power Mode",
      desmode: "Des Mode",
      regenstat: "Regen Stat",
      batterycharge: "Battery Charge",
      avgcelltemp: "Average Cell Temperature",
      averagecelltemperature: "Average Cell Temperature",
      sootpercentage: "Soot Percentage",
      fuellevel: "Fuel Level"
    };

    if (friendlyNames[normalized]) return friendlyNames[normalized];

    return rawName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  private static getKnownFieldMeta = (
    fieldName: string
  ): Pick<MonitoringParameter, "type" | "unit" | "scale" | "enums"> => {
    const normalized = this.normalizeFieldName(fieldName);

    if (normalized === "regenlevel") {
      return {
        type: "enum",
        unit: null,
        enums: {
          "0": "No regen",
          "1": "Low",
          "2": "Medium",
          "3": "High",
          "4": "Max"
        }
      };
    }

    if (normalized === "immpowermode" || normalized === "inmpowermodeactive") {
      return {
        type: "enum",
        unit: null,
        enums: { "1": "Inactive", "2": "Active" }
      };
    }

    if (normalized === "desmode") {
      return {
        type: "enum",
        unit: null,
        enums: {
          "1": "Work mode 1",
          "2": "Work mode 2",
          "3": "Work mode 3",
          "4": "Work mode 4"
        }
      };
    }

    if (normalized === "soc") {
      return { type: "numeric", unit: "%", scale: { min: 0, max: 100 } };
    }

    if (normalized === "avgcelltemp") {
      return { type: "numeric", unit: "°C", scale: { min: -10, max: 100 } };
    }

    if (normalized === "systemstate" || normalized === "sysfailurestate") {
      return {
        type: "enum",
        unit: null,
        enums: { "1": "Inactive", "2": "Active", "3": "Failure" }
      };
    }

    if (normalized === "regenstat") {
      return {
        type: "enum",
        unit: null,
        enums: { "0": "No regen", "1": "Manual", "2": "Auto" }
      };
    }

    return { type: "numeric", unit: null };
  };

  private static getMockParameters = (): MonitoringParameter[] => [
    {
      id: "RegenLevel",
      name: "Regen level",
      unit: null,
      type: "enum",
      enums: { "0": "No regen", "1": "Manual", "2": "Auto" },
      isActiveField: true
    },
    {
      id: "ImmPowerMode",
      name: "Imm power mode",
      unit: null,
      type: "enum",
      enums: { "1": "Inactive", "2": "Active" },
      isActiveField: true
    },
    {
      id: "DesMode",
      name: "Des Mode",
      unit: null,
      type: "numeric",
      scale: { min: 1, max: 4 },
      isActiveField: true
    },
    {
      id: "RegenStat",
      name: "Regen Stat",
      unit: null,
      type: "enum",
      enums: { "0": "No regen", "1": "Manual", "2": "Auto" },
      isActiveField: true
    },
    {
      id: "BatteryCharge",
      name: "Battery charge",
      unit: "%",
      type: "numeric",
      scale: { min: 0, max: 100 },
      isActiveField: true
    },
    {
      id: "AverageCellTemp",
      name: "Average cell temperature",
      unit: "°C",
      type: "numeric",
      scale: { min: -10, max: 100 },
      isActiveField: true
    },
    {
      id: "SootPercentage",
      name: "Soot Percentage",
      unit: "%",
      type: "numeric",
      scale: { min: 0, max: 100 },
      isActiveField: true
    },
    {
      id: "FuelLevel",
      name: "Fuel level",
      unit: "%",
      type: "numeric",
      scale: { min: 0, max: 100 },
      isActiveField: true
    }
  ];

  private static generateMockTelemetryData = (
    parameters: MonitoringParameter[],
    from: string,
    to: string
  ): TelemetryData[] => {
    let start = moment(from);
    let end = moment(to);

    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      end = moment();
      start = end.clone().subtract(3, "hours");
    }

    const durationMins = Math.max(
      1,
      Math.round(moment.duration(end.diff(start)).asMinutes())
    );

    let stepMins = 5;
    if (durationMins > 60 * 24 * 7) stepMins = 60 * 6;
    else if (durationMins > 60 * 24) stepMins = 60;
    else if (durationMins > 60 * 6) stepMins = 15;

    const timeline: string[] = [];
    let cursor = start.clone();
    while (cursor.isBefore(end) || cursor.isSame(end)) {
      timeline.push(cursor.toISOString());
      cursor = cursor.add(stepMins, "minutes");
    }
    const lastTs = timeline[timeline.length - 1];
    if (!lastTs || !moment(lastTs).isSame(end)) {
      timeline.push(end.toISOString());
    }

    return parameters.map((param) => {
      const history = timeline.map((timestamp, idx) => {
        let value: number | string;

        if (param.type === "enum" && param.enums) {
          const enumValues = Object.keys(param.enums);
          value = enumValues[idx % enumValues.length];
        } else {
          const min = param.scale?.min ?? 0;
          const max = param.scale?.max ?? 100;
          const progress = idx / Math.max(1, timeline.length - 1);
          const oscillation =
            Math.sin(progress * Math.PI * 2) * (max - min) * 0.1;
          value = Number(
            (min + progress * (max - min) + oscillation).toFixed(2)
          );
        }

        return { timestamp, value };
      });

      return {
        parameterId: param.id,
        currentValue:
          history.length > 0 ? history[history.length - 1].value : "-",
        history
      };
    });
  };

  static getTimeseriesFields = async (): Promise<TimeseriesField[]> => {
    const response = await axiosInstance.get("/fields/timeseries");
    return (response.data || []) as TimeseriesField[];
  };

  /**
   * Fetches the available CAN Bus parameters for a specific object
   */
  static getParameters = async (
    objectId: string,
    useMockData = false
  ): Promise<MonitoringParameter[]> => {
    if (useMockData) {
      await delay(600);
      return this.getMockParameters();
    }

    const fields = await this.getTimeseriesFields();

    return fields.map((field) => {
      const fieldMeta = this.getKnownFieldMeta(field.name);
      return {
        id: field.uniqueId,
        name: this.getFriendlyFieldName(field.name),
        unit: fieldMeta.unit,
        type: fieldMeta.type,
        scale: fieldMeta.scale,
        enums: fieldMeta.enums,
        isActiveField: true
      };
    });
  };

  /**
   * Generates mock telemetry data based on parameter list and time range
   */
  static getTelemetryData = async (
    objectId: string,
    parameters: MonitoringParameter[],
    from: string,
    to: string,
    useMockData = false
  ): Promise<TelemetryData[]> => {
    if (useMockData) {
      await delay(250);
      return this.generateMockTelemetryData(parameters, from, to);
    }

    const state = store.getState();
    const accountId =
      state.rootReducer.dashboardReducer.drawerSelectedAccountId;

    const start = moment(from);
    const end = moment(to);
    const durationMins = moment.duration(end.diff(start)).asMinutes();

    let aggregateMinutes = 1;
    if (durationMins > 60 * 24 * 7) aggregateMinutes = 60 * 6;
    else if (durationMins > 60 * 24) aggregateMinutes = 60;
    else if (durationMins > 60 * 6) aggregateMinutes = 15;
    else aggregateMinutes = 5;

    const promises = parameters.map(async (param) => {
      try {
        const response = await axiosInstance.get(
          `/accounts/${accountId}/objects/${objectId}/timeseries`,
          {
            params: {
              FieldId: param.id,
              From: from,
              To: to,
              AggregateMinutes: aggregateMinutes
            }
          }
        );

        const rawData = (response.data || []) as Array<{
          timestamp: string;
          value: number | string;
        }>;
        const fromMoment = moment(from);
        const toMoment = moment(to);
        const dedupedMap = new Map<string, number | string>();
        rawData.forEach((point) => {
          if (!point?.timestamp) return;
          dedupedMap.set(point.timestamp, point.value);
        });
        const dedupedData = Array.from(dedupedMap.entries()).map(
          ([timestamp, value]) => ({
            timestamp,
            value: this.adaptTelemetryValueForField(param.name, value)
          })
        );

        const data = dedupedData.filter((point) => {
          const ts = moment(point.timestamp);
          return (
            ts.isValid() &&
            (ts.isSame(fromMoment) || ts.isAfter(fromMoment)) &&
            (ts.isSame(toMoment) || ts.isBefore(toMoment))
          );
        });

        const sorted = data.sort(
          (a, b) =>
            moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf()
        );

        return {
          parameterId: param.id,
          currentValue:
            sorted.length > 0 ? sorted[sorted.length - 1].value : "-",
          history: sorted.map((d) => ({
            timestamp: d.timestamp,
            value: d.value
          }))
        };
      } catch (e) {
        console.error(
          `Failed to fetch telemetry data for parameter ${param.name}:`,
          e
        );
        return {
          parameterId: param.id,
          currentValue: "-",
          history: []
        };
      }
    });

    return await Promise.all(promises);
  };

  /**
   * Fetches mock KPIs
   */
  static getKPIs = async (
    _objectId: string,
    from: string,
    to: string,
    useMockData = false,
    parameters: MonitoringParameter[] = [],
    telemetryData: TelemetryData[] = []
  ): Promise<MonitoringKPI[]> => {
    if (!useMockData) {
      const normalize = (value: string) =>
        value.toLowerCase().replace(/[_-]/g, " ").replace(/\s+/g, " ").trim();

      const kpiDefinitions: Array<{
        id: string;
        title: string;
        defaultUnit: string;
        matches: RegExp[];
      }> = [
        {
          id: "inverter_hours",
          title: "Total Operating",
          defaultUnit: "h",
          matches: [/inverterhours/, /inverter hours/]
        },
        {
          id: "motor_hours",
          title: "Engine Operating",
          defaultUnit: "h",
          matches: [/motorhours/, /motor hours/]
        },
        {
          id: "crd_hours",
          title: "Catalyst device operating hours",
          defaultUnit: "h",
          matches: [/crdhours/, /crd hours/]
        }
      ];

      const baseKpis = kpiDefinitions
        .map((definition) => {
          const matchedParam = parameters.find((param) => {
            const label = normalize(`${param.name} ${param.id}`);
            return definition.matches.some((matcher) => matcher.test(label));
          });

          const matchedSeries = matchedParam
            ? telemetryData.find(
                (series) => series.parameterId === matchedParam.id
              )
            : undefined;

          const numericHistory = (matchedSeries?.history ?? [])
            .map((point) => {
              const value =
                typeof point.value === "number"
                  ? point.value
                  : Number(point.value);
              return Number.isFinite(value)
                ? { timestamp: point.timestamp, value: Number(value) }
                : null;
            })
            .filter((point): point is { timestamp: string; value: number } =>
              Boolean(point)
            );

          const latestValue =
            numericHistory[numericHistory.length - 1]?.value ?? 0;
          const valueFactor =
            definition.id === "motor_hours" || definition.id === "crd_hours"
              ? 0.05
              : 1;
          const scaledHistory =
            valueFactor === 1
              ? numericHistory
              : numericHistory.map((point) => ({
                  ...point,
                  value: Number((point.value * valueFactor).toFixed(2))
                }));
          const scaledLatest = latestValue * valueFactor;

          return {
            id: definition.id,
            title: definition.title,
            currentValue: Number(scaledLatest.toFixed(2)),
            unit: matchedParam?.unit ?? definition.defaultUnit,
            sparklineData: scaledHistory
          };
        })
        .slice(0, 3);

      const normalizeField = (name: string) => this.normalizeFieldName(name);

      const dynamicKpiMatchers: Array<{
        id: string;
        title: string;
        fieldNames: string[];
      }> = [
        { id: "kpi_des_mode", title: "Des Mode", fieldNames: ["desmode"] },
        {
          id: "kpi_imm_power_mode",
          title: "Imm Power Mode",
          fieldNames: ["inmpowermodeactive", "immpowermode"]
        },
        {
          id: "kpi_system_state",
          title: "System State",
          fieldNames: ["systemstate"]
        },
        {
          id: "kpi_sys_failure_state",
          title: "System Failure State",
          fieldNames: ["sysfailurestate", "systemfailurestate"]
        }
      ];

      const dynamicKpis: MonitoringKPI[] = [];

      const findParamByFieldNames = (fieldNames: string[]) => {
        for (const name of fieldNames) {
          const found = parameters.find(
            (param) => normalizeField(param.name) === name
          );
          if (found) return found;
        }
        return undefined;
      };

      for (const matcher of dynamicKpiMatchers) {
        const matchedParam = findParamByFieldNames(matcher.fieldNames);
        if (!matchedParam) continue;

        const series = telemetryData.find(
          (t) => t.parameterId === matchedParam.id
        );
        if (!series?.history?.length) continue;

        const meta = this.getKnownFieldMeta(matchedParam.name);
        const enums = meta.enums ?? matchedParam.enums;
        const lastRaw = series.history[series.history.length - 1]?.value;
        let valueLabel: string | undefined;
        if (lastRaw !== undefined && lastRaw !== null) {
          if (enums) {
            valueLabel = enums[lastRaw.toString()] ?? String(lastRaw);
          } else {
            valueLabel = String(lastRaw);
          }
        }

        const numericHistory = series.history
          .map((point) => {
            const value =
              typeof point.value === "number"
                ? point.value
                : Number(point.value);
            return Number.isFinite(value)
              ? { timestamp: point.timestamp, value: Number(value) }
              : null;
          })
          .filter((point): point is { timestamp: string; value: number } =>
            Boolean(point)
          );

        const latestNumeric =
          numericHistory[numericHistory.length - 1]?.value ?? 0;

        dynamicKpis.push({
          id: matcher.id,
          title: matcher.title,
          currentValue: latestNumeric,
          unit: matchedParam.unit ?? "",
          sparklineData: numericHistory,
          valueLabel
        });
      }

      return [...baseKpis, ...dynamicKpis];
    }

    await delay(500);

    const totalHours = Math.max(
      1,
      Math.round(moment.duration(moment(to).diff(moment(from))).asHours())
    );
    const points = Math.min(36, Math.max(10, totalHours));
    const stepHours = Math.max(1, totalHours / points);

    const generateSparkline = (
      baseline: number,
      trendPerStep: number,
      waveAmplitude: number,
      waveFrequency: number,
      jitterAmplitude: number
    ) => {
      return Array.from({ length: points }).map((_, i) => {
        const timestamp = moment(from)
          .clone()
          .add(i * stepHours, "hours")
          .toISOString();
        const trend = trendPerStep * i;
        const wave = Math.sin(i / waveFrequency) * waveAmplitude;
        const jitter = (Math.random() - 0.5) * jitterAmplitude;
        const value = Number((baseline + trend + wave + jitter).toFixed(2));

        return { timestamp, value };
      });
    };

    const totalOperatingSeries = generateSparkline(420, 0.75, 7, 2.4, 2.2);
    const engineOperatingSeries = generateSparkline(225, -0.45, 10, 1.7, 3.5);
    const catalystOperatingSeries = generateSparkline(192, 0.04, 1.3, 3.1, 0.7);

    return [
      {
        id: "total_operating",
        title: "Total Operating",
        currentValue:
          totalOperatingSeries[totalOperatingSeries.length - 1]?.value ?? 0,
        unit: "h",
        sparklineData: totalOperatingSeries
      },
      {
        id: "engine_operating",
        title: "Engine Operating",
        currentValue:
          engineOperatingSeries[engineOperatingSeries.length - 1]?.value ?? 0,
        unit: "h",
        sparklineData: engineOperatingSeries
      },
      {
        id: "catalyst_device_operating",
        title: "Catalyst Device Operating",
        currentValue:
          catalystOperatingSeries[catalystOperatingSeries.length - 1]?.value ??
          0,
        unit: "h",
        sparklineData: catalystOperatingSeries
      }
    ];
  };

  /**
   * Fetches mock Events
   */
  static getEvents = async (
    objectId: string,
    params: {
      from: string;
      to: string;
      search?: string;
      page?: number;
      pageSize?: number;
    }
  ): Promise<MonitoringEventsResponse> => {
    await delay(700);

    const levels: MonitoringEvent["level"][] = ["Info", "Warning", "Severe"];
    const descriptions = [
      "Regeneration started",
      "Regeneration stopped",
      "High fuel consumption",
      "Battery low",
      "Engine started"
    ];

    // Generate some mock events
    let items = Array.from({ length: 45 }).map((_, i) => ({
      id: `evt-${i}`,
      timestamp: moment(params.to)
        .subtract(Math.random() * 10 * i, "minutes")
        .toISOString(),
      level: levels[Math.floor(Math.random() * levels.length)],
      description: descriptions[Math.floor(Math.random() * descriptions.length)]
    }));

    if (params.search) {
      items = items.filter((e) =>
        e.description.toLowerCase().includes(params.search!.toLowerCase())
      );
    }

    const page = params.page || 1;
    const pageSize = params.pageSize || 10;

    // Simple descending sort by default
    items = items.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    const paginatedItems = items.slice((page - 1) * pageSize, page * pageSize);

    return {
      items: paginatedItems,
      pagination: {
        page,
        pageSize,
        totalCount: items.length
      }
    };
  };
}

export default MonitoringService;
